import { useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useQuery } from "@tanstack/react-query";
import { BuildingIcon, ExternalLinkIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { useTrayStatus } from "components/lib/Notifications";

import type { ChallengeHeader } from "../types";

const MIN_BRIEF_LENGTH = 200;

const stripRichText = (value?: string) => {
  if (!value) {
    return "";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(value, "text/html");
  return doc.body.textContent?.replace(/\s+/g, " ").trim() || "";
};

const getChallengeBriefLength = (
  challenge: Pick<
    ChallengeHeader,
    | "name"
    | "tagline"
    | "description"
    | "instructions"
    | "requirements"
    | "helpfulTips"
    | "reward"
  >,
) =>
  [
    challenge.name,
    challenge.tagline,
    challenge.description,
    challenge.instructions,
    challenge.requirements,
    challenge.helpfulTips,
    challenge.reward,
  ]
    .map((value) => stripRichText(value))
    .filter(Boolean)
    .join(" ").length;

type InnoIntelSearchActionProps = { challenge: ChallengeHeader };

const InnoIntelSearchAction = ({ challenge }: InnoIntelSearchActionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [, setTrayStatus] = useTrayStatus();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStartingSearch, setIsStartingSearch] = useState(false);

  const challengeOrganisation = useMemo(() => {
    if (challenge.ownerOrganisation?._id === challenge.organisation) {
      return challenge.ownerOrganisation;
    }
    if (user?.ownerOrganisation?._id === challenge.organisation) {
      return user?.ownerOrganisation;
    }
    return user?.ownerOrganisations?.find(
      (organisation) => organisation._id === challenge.organisation,
    );
  }, [
    challenge.organisation,
    challenge.ownerOrganisation,
    user?.ownerOrganisation,
    user?.ownerOrganisations,
  ]);
  const canManageChallengeBusinesses = useMemo(
    () =>
      util.hasPermission(
        user,
        "org.manageChallenges",
        challenge.organisation,
      ) || util.hasPermission(user, "challenge.viewSettings", challenge._id),
    [challenge._id, challenge.organisation, user],
  );
  const hasInnointelAccess = useMemo(
    () =>
      !!challengeOrganisation?.enabledFeatures?.includes(
        "innovationIntelligence",
      ) &&
      util.hasPermission(
        user,
        "org.innovationIntelligence",
        challenge.organisation,
      ),
    [challenge.organisation, challengeOrganisation, user],
  );
  const hasSufficientBrief = useMemo(
    () => getChallengeBriefLength(challenge) >= MIN_BRIEF_LENGTH,
    [challenge],
  );

  const canQueryLinkedSearch =
    !!user?._id &&
    !!challenge._id &&
    hasInnointelAccess &&
    canManageChallengeBusinesses;
  const userId = user?._id;
  const searchTaskQuery = useQuery(
    initUseQuery(
      api.users.getLatestLongRunningTask,
      [userId!, "challenge", challenge._id, "challengeInnointelSearch"],
      {
        isEnabled: canQueryLinkedSearch,
        refetchInterval: (data) => {
          if (data.state?.data?.task?.status === "pending") {
            return 3000;
          }
          return false;
        },
      },
    ),
  );
  const searchTask = searchTaskQuery.data?.task;

  const canCreateSearch =
    hasInnointelAccess && canManageChallengeBusinesses && hasSufficientBrief;
  const canShowAction =
    hasInnointelAccess && canManageChallengeBusinesses && canCreateSearch;

  if (!user?._id || !canShowAction) {
    return null;
  }

  return (
    <>
      <Button
        size={theme.sizes.isMobile ? "small" : undefined}
        loading={searchTaskQuery.isLoading}
        onClick={() => {
          if (searchTask) {
            navigate(`/innointel/searches/${searchTask.context?.searchId}`);
            return;
          }
          setIsModalOpen(true);
        }}
        color={!searchTask ? "secondary" : undefined}
      >
        <Icon
          icon={
            searchTask?.status === "pending"
              ? undefined
              : searchTask
                ? ExternalLinkIcon
                : BuildingIcon
          }
        />
        {searchTask?.status === "pending" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
            }}
          >
            <Loader size="small" style={{ marginRight: 8 }} />
            {t("challenge.businesses.loadingTitle")}
          </div>
        ) : searchTask ? (
          t("challenge.businesses.viewTitle")
        ) : (
          t("challenge.businesses.findTitle")
        )}
      </Button>
      <Modal
        open={isModalOpen}
        onClose={() => {
          if (!isStartingSearch) {
            setIsModalOpen(false);
          }
        }}
        size="small"
      >
        <ModalHeader>{t("challenge.businesses.findTitle")}</ModalHeader>
        <ModalContent>
          <p>
            We will automatically analyse this {t("generic.challenge")} brief
            and derive suitable parameters for a business search based on its
            contents.
          </p>
          <p>
            You can stay on this {t("generic.challenge")} page while the search
            runs. We will notify you when the first results are ready to review.
          </p>
          <p>
            If no location can be determined from the {t("generic.challenge")},
            the search will default to the United Kingdom.
          </p>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => setIsModalOpen(false)}
            disabled={isStartingSearch}
          >
            Cancel
          </Button>
          <Button
            loading={isStartingSearch}
            onClick={() => {
              setIsStartingSearch(true);
              api.users.createLongRunningTask(
                user._id,
                {
                  forType: "challenge",
                  forId: challenge._id,
                  type: "challengeInnointelSearch",
                },
                () => {
                  setIsStartingSearch(false);
                  setIsModalOpen(false);
                  setTrayStatus({ open: true, activeIndex: 0 });
                  api.invalidateQueries(api.notifications.get, []);
                  api.invalidateQueries(api.users.getLatestLongRunningTask, [
                    user._id,
                    "challenge",
                    challenge._id,
                    "challengeInnointelSearch",
                  ]);
                },
                (error) => {
                  setIsStartingSearch(false);
                  toast.error(
                    error?.message ||
                      "We could not start the business search right now.",
                  );
                },
              );
            }}
            color="primary"
          >
            Start search
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default InnoIntelSearchAction;
